import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-warenkorbabbrueche-reduzieren";
const TITLE = "Warenkorbabbrüche reduzieren: Ursachen und Gegenmittel";
const DESCRIPTION =
  "Warum Kunden den Warenkorb verlassen und was zuverlässig hilft: Versandkosten, Zahlungsarten, Lieferzeit, Gastbestellung und Rückgewinnung per E-Mail.";

const faqs = [
  {
    q: "Wie hoch ist die durchschnittliche Warenkorbabbruchrate?",
    a: "Im E-Commerce liegt sie bei etwa 70 Prozent, mobil oft über 75 Prozent. Ein Wert unter 65 Prozent gilt als gut.",
  },
  {
    q: "Was ist der häufigste Grund für Kaufabbrüche?",
    a: "Unerwartete Zusatzkosten im letzten Schritt, vor allem Versandkosten. Wer Versandkosten und Lieferzeit bereits auf der Produktseite nennt, verliert im Checkout deutlich weniger Kunden.",
  },
  {
    q: "Hilft eine Gastbestellung wirklich?",
    a: "Ja. Ein Kontozwang ist einer der stärksten einzelnen Abbruchgründe. Das Konto kann nach dem Kauf angeboten werden, wenn die Bestellung bereits abgeschlossen ist.",
  },
  {
    q: "Wie viele Abbrecher hole ich per E-Mail zurück?",
    a: "Gut gemachte Abbrecher-Strecken gewinnen typischerweise 5 bis 12 Prozent der abgebrochenen Warenkörbe zurück. Die erste Nachricht sollte innerhalb einer Stunde kommen.",
  },
  {
    q: "Sind Rabatte im Abbrecher-Mailing sinnvoll?",
    a: "Nur dosiert. Wer immer rabattiert, erzieht Kunden zum Abbruch. Besser ist es, zuerst den eigentlichen Zweifel zu beantworten: Lieferzeit, Rückgabe, Verfügbarkeit.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-warenkorbabbrueche-reduzieren")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Checkout"
      title="Warenkorbabbrüche reduzieren: die Reihenfolge, die wirkt"
      intro="Sieben von zehn gefüllten Warenkörben werden nie bestellt. Der größte Teil davon ist nicht verloren, sondern das Ergebnis vermeidbarer Reibung, meist in den letzten drei Klicks."
      source="ratgeber-warenkorbabbrueche"
      sections={[
        {
          h: "Die Ursachen in der Reihenfolge ihrer Wirkung",
          p: [
            "Diese Liste entspricht der Häufigkeit, mit der ich die Gründe in Shop-Daten und Sitzungsaufzeichnungen finde.",
          ],
          list: [
            "Versandkosten erscheinen erst im Checkout",
            "Lieferzeit unklar oder erst nach Adresseingabe sichtbar",
            "Kontozwang statt Gastbestellung",
            "Fehlende gewohnte Zahlungsart, im DACH-Raum vor allem Rechnungskauf und PayPal",
            "Zu viele Pflichtfelder und ein mehrstufiges Formular ohne Fortschritt",
            "Rabattcode-Feld, das aktiv zur Suche nach Gutscheinen einlädt",
          ],
        },
        {
          h: "Transparenz vor dem Checkout",
          p: [
            "Der wirksamste Eingriff passiert nicht im Checkout, sondern davor: Versandkosten, Lieferzeit und Rückgabebedingungen direkt am Preis auf der Produktseite. Damit trifft der Kunde die Entscheidung, bevor er investiert hat.",
            "Ein zweiter Hebel ist die Fortschrittsanzeige zur versandkostenfreien Schwelle im Warenkorb, sie erhöht in der Praxis sowohl Abschlussrate als auch Warenkorbhöhe.",
          ],
        },
        {
          h: "Checkout selbst entschlacken",
          p: [
            "Weniger Felder, sinnvolle Autovervollständigung für Adressen, klare Fehlermeldungen und Express-Zahlungsarten oben. Auf mobilen Geräten entscheidet zusätzlich die Tastaturart pro Feld über die Abbruchrate.",
            "Auf Shopify Plus lassen sich Vertrauenselemente und Hinweise direkt im Checkout platzieren; in Standardplänen wirken dieselben Inhalte auf Warenkorb- und Produktseite.",
          ],
        },
        {
          h: "Rückgewinnung: E-Mail und Push",
          p: [
            "Drei Nachrichten reichen: nach einer Stunde eine sachliche Erinnerung, nach 24 Stunden die Antwort auf den häufigsten Zweifel, nach drei Tagen ein letzter Hinweis. Rabatt frühestens in der dritten Nachricht, wenn überhaupt.",
            "Wichtig ist die Messung als eigener Kanal: Rückgewonnener Umsatz gehört nicht in dieselbe Zahl wie organische Kaufabschlüsse, sonst verzerrt er jede Bewertung.",
          ],
        },
        {
          h: "Was das in Euro bedeutet",
          p: [
            "Bei 25.000 Sessions, 70 € Warenkorb und einer Verbesserung der Kaufabschlussrate um 0,4 Prozentpunkte entstehen rund 7.000 € Mehrumsatz pro Monat. Die genannten Eingriffe sind fast alle in wenigen Tagen umsetzbar.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/blog/shopify-checkout-optimieren", label: "Checkout optimieren" },
        { to: "/ratgeber/shopify-mobile-optimierung", label: "Mobile Optimierung" },
        { to: "/roi-rechner", label: "Potenzial berechnen" },
      ]}
    />
  );
}
